import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { withRouter } from "react-router-dom";
import {
  AddressWithDuplicate,
  ErrorMsg,
  Button,
  Warning,
  InputWithValidation
} from "@onegovbugs/onegov-react-components";
import {
  isCompleted,
  isAustralianPhoneNumberOrNull,
  isValidEmail,
  isSelected,
  validate,
  scrollToError
} from "../../../utils/Validation";
import styles from "./ApplicantDetail.module.scss";
import {
  storePersonalDetails,
  saveApplication,
  process2FA,
} from "../../../redux/actions";
import { RedirectIfCaptchaNotSelected } from "../../../utils/Redirect";
import ScrollToTop from "../../../utils/ScrollToTop";
import {toCamelCase, format2FAAddress} from "../../../utils/FormatHelper";
import { getAddresUrlPath } from '../../../utils/AddressHelper';

const ApplicantDetail = (props) => {
  RedirectIfCaptchaNotSelected(props);
  ScrollToTop();

  const dispatch = useDispatch();
  const emailRef = useRef();

  // Existing Customer Info
  const existingCustomerDetails = useSelector((state) => state.customerValidationDetail);

  // Two FA
  const twoFA = useSelector((state) => state.twoFA);
  const twoFAData =
    twoFA && twoFA.data
      ? twoFA.data
      : { firstName: "", lastName: "", day: "", month: "", year: "" };
  const twoFAVerified =
    twoFA && twoFA.twoFaVerified ? twoFA.twoFaVerified : false;

  // Personal Info
  const personalDetails = useSelector((state) => state.personalDetails);

  const [isTwoFaPopulated, setTwoFaPopulated] = useState(
    personalDetails && personalDetails.isTwoFaPopulated
      ? personalDetails.isTwoFaPopulated
      : false
  );
  const [title, setTitle] = useState(
    personalDetails && personalDetails.title ? personalDetails.title : null
  );
  const [emailAddress, setEmailAddress] = useState(
    personalDetails && personalDetails.emailAddress
      ? personalDetails.emailAddress
      : ""
  );
  const [isEmailAddressChanged, setEmailAddressChanged] = useState(false);
  const [isEmailAddressValid, setEmailAddressValid] = useState(!!personalDetails.isEmailAddressValid);
  const [isEmailAddressSkipped, setEmailAddressSkipped] = useState(false);

  const [dayTimeTelephoneNumber, setDayTimeTelephoneNumber] = useState(
    personalDetails && personalDetails.dayTimeTelephoneNumber
      ? personalDetails.dayTimeTelephoneNumber
      : ""
  );
  const [mobileNumber, setMobileNumber] = useState(
    personalDetails && personalDetails.mobileNumber
      ? personalDetails.mobileNumber
      : ""
  );
  const [
    residentialAddressPlaceHolder,
    setAddressResidentialPlaceholder,
  ] = useState(null);
  const [postalAddressPlaceHolder, setAddressPostalPlaceholder] = useState(
    null
  );
  // NOTE: You can't load the address data in the placeholder variable. The placeholder HAS to be a string, and
  // should come from the 2FA data only, not to be mixed with the actual data that the address can capture and contain.

  const [postalAddress, setPostalAddress] = useState(
    personalDetails && personalDetails.secondaryAddress
      ? { ...personalDetails.secondaryAddress }
      : {}
  );
  const [residentialAddress, setResidentialAddress] = useState(
    personalDetails && personalDetails.address
      ? { ...personalDetails.address }
      : {}
  );
  const [sameAsResidentialFlag, setSameAsResidentialFlag] = useState(
    personalDetails &&
      typeof personalDetails.isSameAsPrimaryAddress === "boolean"
      ? personalDetails.isSameAsPrimaryAddress
      : false
  );
  const [isResidentialAddressValid, setResidentialAddressValid] = useState(false);
  const [isPostalAddressValid, setPostalAddressValid] = useState(false);
  const [isAddressValidating, setAddressValidating] = useState(false);
  const addressRef = useRef();
  useEffect(() => {
    if (isTwoFaPopulated != true) {
      const _title =
        twoFAData.title && twoFAData.title != ""
          ? twoFAData.title
          : personalDetails.title;
      const _mobileNumber =
        twoFAData.mobileNumber && twoFAData.mobileNumber != ""
          ? twoFAData.mobileNumber
          : personalDetails.mobileNumber;
      const _emailAddress =
        twoFAData.emailAddress && twoFAData.emailAddress != ""
          ? twoFAData.emailAddress
          : personalDetails.emailAddress;
      const _dayTimeTelephoneNumber =
        twoFAData.phoneNumber && twoFAData.phoneNumber != ""
          ? twoFAData.phoneNumber
          : personalDetails.dayTimeTelephoneNumber;
      setMobileNumber(_mobileNumber);
      setEmailAddress(_emailAddress);
      if(emailRef && emailRef.current) {
        emailRef.current.setEmail(_emailAddress);
        setEmailAddressValid(true);
      }
      setDayTimeTelephoneNumber(_dayTimeTelephoneNumber);
      
      // populate title
      const twoFATitle = titleOptions
      .filter((x) => x.label == _title)
      .map((item) => {
        return { value: item.value, label: item.label };
      });
      setTitle(twoFATitle[0]);

      if (twoFAData.addressses) {
        const formattedResidentialAddress = format2FAAddress(
          twoFAData.addressses,
          "Residential"
        );
        const formattedPostalAddress = format2FAAddress(
          twoFAData.addressses,
          "Postal"
        );

        addressRef.current.setValue({
          isSameAddress: formattedResidentialAddress === formattedPostalAddress,
        });
        setAddressResidentialPlaceholder(formattedResidentialAddress);
        setAddressPostalPlaceholder(formattedPostalAddress);

        // if (formattedResidentialAddress === formattedPostalAddress) {
        //   setSameAsResidentialFlag(true);
        // }
        // else{
        //   setSameAsResidentialFlag(false);
        // }
      }
    }
  }, [twoFA]);

  useEffect(() => {
    if (twoFAData.addressses) {
      let formattedResidentialAddress;
      let formattedPostalAddress;

      let internationalResAddress = undefined;
      const addressNewValue = {};
      if (
        residentialAddress.addressNotChanged ||
        typeof residentialAddress.addressNotChanged === "undefined"
      ) {
        const type = "Residential";
        const twoFAResidentialAddress = twoFAData.addressses.find(
          (a) => a.addressType === type
        );

        if (twoFAResidentialAddress !== undefined){
          const residentialAddressCountry = twoFAResidentialAddress.country;

          if (
            typeof residentialAddressCountry === undefined ||
            residentialAddressCountry.toLowerCase() === "australia"
          ) {
            formattedResidentialAddress = format2FAAddress(
              twoFAData.addressses,
              type
            );
            setAddressResidentialPlaceholder(formattedResidentialAddress);
          } else {
            internationalResAddress = {
              international: true,
              addressLine: twoFAResidentialAddress.addressLine,
              suburb: twoFAResidentialAddress.suburb,
              state: twoFAResidentialAddress.state,
              country: toCamelCase(twoFAResidentialAddress.country),
              postcode: twoFAResidentialAddress.postCode,
            };

            if (internationalResAddress) {
              addressNewValue.firstAddress = internationalResAddress;
            }

            addressRef.current.setValue(addressNewValue);
          }
        }
    
      }
      let internationalPostalAddress = undefined;
      if (
        postalAddress.addressNotChanged ||
        typeof postalAddress.addressNotChanged === "undefined"
      ) {
        const type = "Postal";
        const twoFAPostalAddress = twoFAData.addressses.find(
          (a) => a.addressType === type
        );
        if (twoFAPostalAddress !== undefined){
          const postalAddressCountry = twoFAPostalAddress.country;
        if (
          typeof postalAddressCountry === "undefined" ||
          postalAddressCountry.toLowerCase() === "australia"
        ) {
          formattedPostalAddress = format2FAAddress(
            twoFAData.addressses,
            "Postal"
          );
          setAddressPostalPlaceholder(formattedPostalAddress);
        } else {
          internationalPostalAddress = {
            international: true,
            addressLine: twoFAPostalAddress.addressLine,
            suburb: twoFAPostalAddress.suburb,
            state: twoFAPostalAddress.state,
            country: toCamelCase(twoFAPostalAddress.country),
            postcode: twoFAPostalAddress.postCode,
          };

          if (internationalPostalAddress) {
            addressNewValue.secondAddress = internationalPostalAddress;
          }
          addressRef.current.setValue(addressNewValue);
        }
        }
        
      }
      
      // if (typeof formattedResidentialAddress === "undefined" && typeof formattedPostalAddress === "undefined"){
      //   sameAsAddress = JSON.stringify(internationalResAddress) === JSON.stringify(internationalPostalAddress)
      // }
      // else{
      //   sameAsAddress = formattedResidentialAddress === formattedPostalAddress
      // }
      // const addressNewValue = {
      //   isSameAddress: sameAsAddress
      // };
      // const addressNewValue = {};
      // if (internationalResAddress) {
      //   addressNewValue.firstAddress = internationalResAddress;
      // }
      // if (internationalPostalAddress) {
      //   addressNewValue.secondAddress = internationalPostalAddress;
      // }
      // addressRef.current.setValue(addressNewValue);
    
      // addressRef.current.setValue({
      //   isSameAddress: formattedResidentialAddress === formattedPostalAddress,
      // });
    }
  }, []);

  const getFinalInternationalAdddress = (addressValue, type) =>{
    let finalAdddress;

    if (twoFAData.addressses === null || twoFAData.addressses === undefined){
      return getFinalAdddress(addressValue);
    }
    const address = twoFAData.addressses.find(
      (a) => a.addressType === type
    );
    if (JSON.stringify(addressValue) === JSON.stringify(address)){
      finalAdddress = {
        addressNotChanged: true
      };
    }
    else{
      finalAdddress = {
        addressNotChanged: false,
        ...addressValue
      };
    }
    return finalAdddress;
  }

  const getFinalAdddress = (adddressValue, type) => {
    let finalAdddress;

    // addressNotChanged is not updated to false when initially saved with true as a result of initial save
    // without changing address so the addressValue has addressNotChanged set as true
    var modifiedAddress = removeAddressNotChanged(adddressValue);
    
    if (
      adddressValue.lookupValue ||
      adddressValue.enterManually == true 
    ) {
      finalAdddress = {
        addressNotChanged: false,
        ...modifiedAddress,
      };
    } else {
      finalAdddress = {
        addressNotChanged: true,
        addressType: type,
      };
    }
    return finalAdddress;
  };  

  const removeAddressNotChanged = (adddressValue) => {    
      const {addressNotChanged, ...rest} = adddressValue;
      return rest;    
  };

  // Validation
  const validators = [
    {
      fieldName: "title",
      fieldValue: title,
      isValid: isSelected,
    },
    {
      fieldName: "dayTimeTelephoneNumber",
      fieldValue: dayTimeTelephoneNumber,
      isValid: (v) => isCompleted(v) && isAustralianPhoneNumberOrNull(v),
    },
    {
      fieldName: "mobileNumber",
      fieldValue: mobileNumber,
      isValid: (v) => isAustralianPhoneNumberOrNull(v),
    },
    {
      fieldName: "email",
      fieldValue: emailAddress,
      isValid: v =>  (!isEmailAddressChanged && isValidEmail(v) && isEmailAddressValid) || isEmailAddressSkipped
    },
    {
      fieldName: "addressResidential",
      fieldValue: {
        sameAs: sameAsResidentialFlag,
        addressResidential: residentialAddress,
        addressPostal: postalAddress,
      },
      isValid: (v) => isResidentialAddressValid || (!v.addressResidential.lookupValue &&
        !v.addressResidential.enterManually &&
        residentialAddressPlaceHolder)
    },
    {
      fieldName: "addressPostal",
      fieldValue: {
        sameAs: sameAsResidentialFlag,
        addressResidential: residentialAddress,
        addressPostal: postalAddress,
      },
      isValid: (v) => isPostalAddressValid || (!v.addressPostal.lookupValue &&
        !v.addressPostal.enterManually &&
        postalAddressPlaceHolder)
    },
  ];

  useEffect(() => {
    validate(validators, setErrors);
  }, [title, dayTimeTelephoneNumber, mobileNumber, emailAddress, isEmailAddressChanged, residentialAddress, postalAddress]);

  const [errors, setErrors] = useState({
    title: false,
    dayTimeTelephoneNumber: false,
    mobileNumber: false,
    email: false,
    addressResidential: false,
    addressPostal: false,
  });

  const [showErrors, setShowErrors] = useState(false);
  const titleOptions = [
    {
      value: "mr",
      label: "Mr",
    },
    {
      value: "mrs",
      label: "Mrs",
    },
    {
      value: "miss",
      label: "Miss",
    },
    {
      value: "miss",
      label: "Ms",
    },
    {
      value: "sir",
      label: "Sir",
    },
    {
      value: "dr",
      label: "Dr",
    },
    {
      value: "prof",
      label: "Prof",
    },
    {
      value: "assocprof",
      label: "Assoc Prof",
    },
    {
      value: "rev",
      label: "Rev",
    },
    {
      value: "master",
      label: "Master",
    },
    {
      value: "father",
      label: "Father",
    },
    {
      value: "pastor",
      label: "Pastor",
    },
    {
      value: "sister",
      label: "Sister",
    },
  ];
  // store and save to database
  const storeData = (finished) => {
    const personalDetails = {
      finished: finished,
      isTwoFaPopulated: true,
      title,
      firstname: twoFAData.firstName,
      lastname: twoFAData.lastName,
      dayOfBirth: twoFAData.day,
      monthOfBirth: twoFAData.month,
      yearOfBirth: twoFAData.year,
      emailAddress: emailAddress,
      mobileNumber,
      dayTimeTelephoneNumber,
      address: residentialAddress.international ? getFinalInternationalAdddress(residentialAddress, "Residential") : getFinalAdddress(residentialAddress),
      secondaryAddress: postalAddress.international ? getFinalInternationalAdddress(postalAddress, "Postal") : getFinalAdddress(postalAddress),
      isSameAsPrimaryAddress: sameAsResidentialFlag,
      isEmailAddressValid,
      step: 1,
    };
    dispatch(storePersonalDetails(personalDetails));
  };

  return (
    <div className={styles.mainCont}>
      <div className={"row"}>
      
        <div className={"col-12"}>
          <div className={styles.shadowBox}>
            {twoFAVerified && (
              <Warning type={"success"}>
                <p>Record successfully matched.</p>
              </Warning>
            )}
            
            <h3>{twoFAData.firstName + " " + twoFAData.lastName}</h3>
            <p>
              Date of birth:{" "}
              {twoFAData.day + "/" + twoFAData.month + "/" + twoFAData.year}
            </p>
            <div className={"row " + styles.topMargin24}>
              <div className={"col-12"}>
                <Button
                  onClick={() => {
                    dispatch(
                      storePersonalDetails({
                        isTwoFaPopulated: false,
                      })
                    );
                    dispatch(
                      process2FA((TwoFaUrl) => {
                        sessionStorage["nativespeciesTwoFASource"] =
                          "applicant-detail";
                        window.location = TwoFaUrl;
                      })
                    );
                  }}
                >
                  Change Details
                </Button>
              </div>
            </div>
          </div>
        </div>
      
        <div className={"col-12 col-sm-6"}>
          <label htmlFor="title">Title</label>
          <Select
            id={"inputTitle"}
            value={title}
            onChange={setTitle}
            className={styles.select}
            options={titleOptions}
          />
          {errors.title && showErrors ? (
            <ErrorMsg
              className={"ErrorMsg_error"}
              type={"error"}
              description={"Title is required."}
            />
          ) : null}
        </div>
      </div>

      <hr className={styles.hrLine} />

      <div className={"row"}>
        <div className={"col-12"}>
          <h2 className={styles.headingH2}>Contact Details</h2>
        </div>
      </div>
      <div className={"row"}>
        <div className={"col-12 col-sm-6"}>
          <label htmlFor={"dayTimeTelephoneNumber"}>
            Daytime telephone number
          </label>
          <input
            id={"dayTimeTelephoneNumber"}
            className={styles.inputBox}
            placeholder="Daytime telephone number"
            value={dayTimeTelephoneNumber}
            onChange={(obj) => {
              setDayTimeTelephoneNumber(obj.target.value);
            }}
          />
          {errors.dayTimeTelephoneNumber && showErrors ? (
            <ErrorMsg
              className={"ErrorMsg_error"}
              type={"error"}
              description={"Daytime telephone number is invalid."}
            />
          ) : null}
        </div>
        <div className="col-12 col-sm-6">
          <label htmlFor={"mobileNumber"}>Mobile numbers (Optional)</label>
          <input
            id={"mobileNumber"}
            className={styles.inputBox}
            placeholder="Mobile number"
            value={mobileNumber}
            onChange={(obj) => {
              setMobileNumber(obj.target.value);
            }}
          />
          {errors.mobileNumber &&
          showErrors ? (
            <ErrorMsg
              className={"ErrorMsg_error"}
              type={"error"}
              description={"Mobile number is invalid."}
            />
          ) : null}
        </div>
      </div>
      <div className={"row"}>
        <div className={"col-12 col-sm-6"}>
          <InputWithValidation
            hideErrors
            ref={emailRef}
            type={'email'}
            hasError={showErrors && errors.email}
            baseUrl={process.env.REACT_APP_BASE_URL}
            onChange={(v, changed) => {
              setEmailAddressChanged(changed);
              setEmailAddress(v);
            }}
            isValid={isEmailAddressValid}
            className={styles.verifiableInput}
            onSkipChange={setEmailAddressSkipped}
            onVerificationChange={v => {
              setEmailAddressValid(v);
              if(v) {
                setEmailAddressChanged(false);
              }
            }}
            labels={{
              mainInput: 'Email address'
            }}
            value={{
              email: emailAddress
            }}
          />
          {errors.email && showErrors ? (
            <ErrorMsg
              className={"ErrorMsg_error"}
              type={"error"}
              description={"Email address is invalid or has not verified."}
            />
          ) : null}
        </div>
      </div>
      <hr className={styles.hrLine} />
      <div className={"row"}>
        <div className={"col-12"}>
          <AddressWithDuplicate
            autoValidate
            international
            firstAddressLabel={"Residential"}
            secondAddressLabel={"Postal"}
            baseUrl= { getAddresUrlPath() }
            ref={addressRef}
            showErrors={{
              firstAddress: showErrors && errors.addressResidential,
              secondAddress: showErrors && errors.addressPostal,
            }}
            onChange={(data) => {
              setResidentialAddress(data.firstAddress);
              setPostalAddress(data.secondAddress);
              setSameAsResidentialFlag(data.isSameAddress);
            }}
            placeholder={{
              firstAddress: residentialAddressPlaceHolder,
              secondAddress: postalAddressPlaceHolder,
            }}
            value={{
              isSameAddress: sameAsResidentialFlag,
              firstAddress: residentialAddress,
              secondAddress: postalAddress,
            }}
            onValidationChange={v => {
              setResidentialAddressValid(v.firstAddressValid);
              setPostalAddressValid(v.secondAddressValid);
            }}
            onLoading={(data) => {
              setAddressValidating(
                !!(
                  data.isFirstAddressValidating ||
                  data.isSecondAddressValidating
                )
              );
            }}
          />
        </div>
      </div>

      <div className={`row ${styles.buttonCont}`}>
        <div className={`col-12`}>
          <Button
            disabled={isAddressValidating}
            onClick={() => {
              if (validate(validators, setErrors)) {
                setShowErrors(false);
                storeData(true);
                dispatch(
                  saveApplication("step1", () => {
                    props.history.push(`../../flora-fauna/dashboard`);
                  })
                );
              } else {
                setShowErrors(true);
                scrollToError();
              }
            }}
          >
            Continue
          </Button>

          <Button
            secondary
            onClick={() => {
              storeData(false);
              dispatch(
                saveApplication("step1", () => {
                  props.history.push(`../../flora-fauna/dashboard`);
                })
              );
            }}
          >
            Save & exit section
          </Button>
        </div>
      </div>
    </div>
  );
};

export default withRouter((props) => <ApplicantDetail {...props} />);
